import React from 'react';
import Spacing from '../../Spacing';
import { pageTitle } from '../../../helpers/PageTitle';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import AvatarSelector, { AvatarDisplay } from '../../AvatarSelector';
import { DEFAULT_AVATAR } from '../../../utils/avatars';
import { useAuth } from '../../../context/AuthContext';

// Animation styles
const animations = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .animate-scale-in {
    animation: scaleIn 0.5s ease-out forwards;
  }

  .stagger-1 {
    animation-delay: 0.1s;
    opacity: 0;
  }

  .stagger-2 {
    animation-delay: 0.2s;
    opacity: 0;
  }

  .stagger-3 {
    animation-delay: 0.3s;
    opacity: 0;
  }
`;

export default function Settings() {
  const navigate = useNavigate();
  const { user: contextUser, refreshUser, logout: authLogout } = useAuth();
  const [user, setUser] = React.useState(contextUser);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(false);
  const [formData, setFormData] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    avatar: DEFAULT_AVATAR.id,
  });
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  // Inject animations
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = animations;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  React.useEffect(() => {
    loadUser();
  }, []);

  async function loadUser() {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        avatar: userData.description || DEFAULT_AVATAR.id,
      });
    } catch (error) {
      console.error('Failed to load user:', error);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  async function handleUpdateProfile(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = authService.getAccessToken();
      const response = await fetch(`http://localhost/cms/users/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          description: formData.avatar,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Update error response:', errorData);
        throw new Error(errorData.errors?.[0]?.message || 'Failed to update profile');
      }

      const updatedUser = await response.json();
      setUser(updatedUser.data);
      setSuccess('Profile updated successfully!');
      setEditing(false);

      // Refresh user data in local state and global context
      await loadUser();
      await refreshUser();
    } catch (error) {
      console.error('Update failed:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function cancelEdit() {
    setEditing(false);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      avatar: user.description || DEFAULT_AVATAR.id,
    });
    setError('');
    setSuccess('');
  }

  pageTitle('Settings');

  if (loading && !user) {
    return (
      <>
        <Spacing lg="140" md="80" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <h2>Loading...</h2>
        </div>
        <Spacing lg="140" md="80" />
      </>
    );
  }

  return (
    <>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <Spacing lg="75" md="60" />
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
        {/* Header */}
        <div className="animate-fade-in-up" style={{ marginBottom: '3rem' }}>
          <h1 className="cs_section_title cs_fs_48 mb-0">Account Settings</h1>
          <p style={{ marginTop: '1rem', opacity: 0.7 }}>
            Manage your profile and account settings
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          {/* Profile Card */}
          <div className="animate-scale-in stagger-1" style={{
            backgroundColor: 'rgba(79, 71, 71, 0.1)',
            padding: '2rem',
            borderRadius: '8px',
            gridColumn: editing ? '1 / -1' : 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 className="cs_section_title cs_fs_24 mb-0">Profile Information</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="cs_btn cs_style_1"
                  style={{ padding: '0.5rem 1rem', fontSize: '14px' }}
                >
                  Edit Profile
                </button>
              )}
            </div>

            {error && (
              <div style={{
                color: 'red',
                padding: '0.75rem',
                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                color: 'green',
                padding: '0.75rem',
                backgroundColor: 'rgba(0, 255, 0, 0.1)',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                {success}
              </div>
            )}

            {!editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <AvatarDisplay avatarId={user?.description || DEFAULT_AVATAR.id} size="large" showName />
                </div>
                <div>
                  <label className="cs_fs_12 cs_semibold cs_primary_color">First Name</label>
                  <p style={{ marginTop: '0.25rem', fontSize: '16px' }}>{user?.first_name || 'Not set'}</p>
                </div>
                <div>
                  <label className="cs_fs_12 cs_semibold cs_primary_color">Last Name</label>
                  <p style={{ marginTop: '0.25rem', fontSize: '16px' }}>{user?.last_name || 'Not set'}</p>
                </div>
                <div>
                  <label className="cs_fs_12 cs_semibold cs_primary_color">Email</label>
                  <p style={{ marginTop: '0.25rem', fontSize: '16px' }}>{user?.email}</p>
                </div>
                <div>
                  <label className="cs_fs_12 cs_semibold cs_primary_color">Role</label>
                  <p style={{ marginTop: '0.25rem', fontSize: '16px' }}>{user?.role?.name || 'Registered User'}</p>
                </div>
                <div>
                  <label className="cs_fs_12 cs_semibold cs_primary_color">Member Since</label>
                  <p style={{ marginTop: '0.25rem', fontSize: '16px' }}>
                    {user?.date_created ? new Date(user.date_created).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateProfile}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label className="cs_fs_12 cs_semibold cs_primary_color">Choose Your Avatar</label>
                    <AvatarSelector
                      selectedAvatar={formData.avatar}
                      onSelect={(avatarId) => setFormData({ ...formData, avatar: avatarId })}
                    />
                  </div>
                  <div>
                    <label className="cs_fs_12 cs_semibold cs_primary_color">First Name</label>
                    <input
                      type="text"
                      className="cs_form_field"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="cs_fs_12 cs_semibold cs_primary_color">Last Name</label>
                    <input
                      type="text"
                      className="cs_form_field"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="cs_fs_12 cs_semibold cs_primary_color">Email</label>
                    <input
                      type="email"
                      className="cs_form_field"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <button
                      type="submit"
                      className="cs_btn cs_style_1"
                      disabled={loading}
                      style={{ flex: 1 }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="cs_btn cs_style_1"
                      style={{
                        flex: 1,
                        backgroundColor: '#6c757d',
                        borderColor: '#6c757d'
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Quick Actions Card */}
          {!editing && (
            <div className="animate-scale-in stagger-2" style={{
              backgroundColor: 'rgba(79, 71, 71, 0.1)',
              padding: '2rem',
              borderRadius: '8px'
            }}>
              <h2 className="cs_section_title cs_fs_24 mb-0" style={{ marginBottom: '1.5rem' }}>
                Quick Actions
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="cs_btn cs_style_1"
                  style={{ width: '100%' }}
                >
                  Back to Dashboard
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="cs_btn cs_style_1"
                  style={{ width: '100%' }}
                >
                  View Blog
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="cs_btn cs_style_1"
                  style={{ width: '100%' }}
                >
                  Contact Support
                </button>
                <button
                  onClick={handleLogout}
                  className="cs_btn cs_style_1"
                  style={{
                    width: '100%',
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Account Stats Card */}
          {!editing && (
            <div className="animate-scale-in stagger-3" style={{
              backgroundColor: 'rgba(79, 71, 71, 0.1)',
              padding: '2rem',
              borderRadius: '8px'
            }}>
              <h2 className="cs_section_title cs_fs_24 mb-0" style={{ marginBottom: '1.5rem' }}>
                Account Statistics
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <div className="cs_fs_12 cs_semibold cs_primary_color">Account Status</div>
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    borderRadius: '4px',
                    color: '#28a745',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    Active
                  </div>
                </div>
                <div>
                  <div className="cs_fs_12 cs_semibold cs_primary_color">Last Login</div>
                  <p style={{ marginTop: '0.5rem', fontSize: '16px' }}>
                    {user?.last_access ? new Date(user.last_access).toLocaleString() : 'Just now'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <Spacing lg="75" md="60" />
    </>
  );
}
